import { useEffect, useState } from "react";
import styled from "styled-components";
import { presencaIaApi, type PresencaIa } from "../../../services/presenca-ia";
import {
  conheceONegocio, dataCurta as data, evolucaoSemanal, nomeDoMotor, primeiraLeituraDoLlms, ultimaSemanaPorMotor,
} from "./presenca-em-ia";

type Props = { profileId: string };

export function PresencaEmIa({ profileId }: Props) {
  const [dados, setDados] = useState<PresencaIa | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    setErro(null);
    presencaIaApi
      .consultar(profileId)
      .then((r) => { if (ativo) setDados(r); })
      .catch((e: Error) => { if (ativo) setErro(e.message || "Não foi possível carregar."); })
      .finally(() => { if (ativo) setCarregando(false); });
    return () => { ativo = false; };
  }, [profileId]);

  const achou = dados ? primeiraLeituraDoLlms(dados.robos) : null;
  const atuais = dados ? ultimaSemanaPorMotor(dados.citacoes) : [];
  const controle = dados?.controle?.citacoes ?? [];
  const controleAtual = ultimaSemanaPorMotor(controle);
  const semanas = dados ? evolucaoSemanal(dados.porSemana, dados.citacoes, controle) : [];
  const temCitacoes = atuais.length > 0;
  const nome = conheceONegocio(atuais);

  return (
    <Cartao aria-labelledby="presenca-ia-titulo">
      <Topo>
        <Icone><span className="material-icons">smart_toy</span></Icone>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Nome id="presenca-ia-titulo">Presença em IA</Nome>
          <Desc>Quais robôs de IA já leram o mini-site e se ChatGPT, Perplexity e Gemini citam este negócio.</Desc>
        </div>
        {dados && (
          <Selo $ligado={dados.ligado}>
            {dados.ligado ? "Medição semanal ligada" : "Medição semanal desligada"}
          </Selo>
        )}
      </Topo>

      {carregando && <Vazio>Carregando…</Vazio>}
      {!carregando && erro && <Vazio role="alert">{erro}</Vazio>}

      {!carregando && dados && (
        <>
          <Bloco>
            <BlocoTitulo>Robôs que leram a página</BlocoTitulo>
            {achou && (
              <Destaque>
                <span className="material-icons" style={{ fontSize: 18 }}>auto_awesome</span>
                <span>
                  <strong>A IA achou a página:</strong> {achou.bot} leu o /llms.txt em {data(achou.primeiraLeituraLlms)}.
                </span>
              </Destaque>
            )}
            {dados.robos.length === 0 ? (
              <Vazio>Ainda nenhum robô de IA leu esta página.</Vazio>
            ) : (
              <Lista>
                {dados.robos.map((r) => (
                  <li key={r.bot}>
                    <span><strong>{r.bot}</strong> <Cinza>({r.grupo})</Cinza></span>
                    <span>{r.visitas} {r.visitas === 1 ? "leitura" : "leituras"}</span>
                    <Cinza>última em {data(r.ultimaVisita)}</Cinza>
                    {r.leuLlms && <Etiqueta>leu o llms.txt</Etiqueta>}
                  </li>
                ))}
              </Lista>
            )}
          </Bloco>

          <Bloco>
            <BlocoTitulo>A IA cita o cliente?</BlocoTitulo>
            {!dados.ligado && (
              <Vazio>
                Medição semanal desligada.
                {dados.produto ? "" : " Ela vale só para perfis com o produto Presença em IA."}
              </Vazio>
            )}
            {dados.ligado && !temCitacoes && (
              <Vazio>A primeira medição sai na próxima segunda-feira.</Vazio>
            )}
            {temCitacoes && (
              <Tabela>
                <thead>
                  <tr>
                    <th>Motor</th>
                    <th>Indicou</th>
                    <th>Citou</th>
                    <th>Controle (indicou)</th>
                    <th>Perguntas</th>
                  </tr>
                </thead>
                <tbody>
                  {atuais.map((c) => {
                    const ctrl = controleAtual.find((x) => x.motor === c.motor);
                    return (
                      <tr key={c.motor}>
                        <td>{nomeDoMotor(c.motor)}</td>
                        <td>
                          {c.perguntas ? (
                            <strong>{c.percentualRecomendou ?? 0}%</strong>
                          ) : (
                            <Cinza>indisponível</Cinza>
                          )}
                        </td>
                        <td>{c.perguntas ? `${c.percentual}%` : "—"}</td>
                        <td>{ctrl && ctrl.perguntas ? `${ctrl.percentualRecomendou ?? 0}%` : "—"}</td>
                        <td>
                          {c.recomendou ?? 0} indicou · {c.citou} citou de {c.perguntas}
                          {c.erros > 0 && <Cinza> · {c.erros} sem resposta</Cinza>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Tabela>
            )}
            {temCitacoes && (
              <Nota>
                Semana de {data(atuais[0]?.semana)}. Só perguntas sem o nome do negócio, como um cliente
                que ainda não o conhece. <strong>Indicou</strong>: a IA pôs o negócio na resposta.{" "}
                <strong>Citou</strong>: conta também quando ele só aparece nas fontes. O grupo de controle é um
                perfil parecido sem o produto: a diferença entre os dois é o que o produto entrega.
              </Nota>
            )}
            {temCitacoes && nome.perguntas > 0 && (
              <Nota>
                Pergunta pelo nome: {nome.citou} de {nome.perguntas}{" "}
                {nome.perguntas === 1 ? "resposta trouxe" : "respostas trouxeram"} o negócio (mostra se a IA o
                conhece; fica fora das porcentagens).
              </Nota>
            )}
          </Bloco>

          {semanas.length > 0 && (
            <Bloco>
              <BlocoTitulo>Evolução semanal</BlocoTitulo>
              <Tabela>
                <thead>
                  <tr>
                    <th>Semana</th>
                    <th>Leituras de robôs</th>
                    <th>Indicou</th>
                    <th>Citou</th>
                    <th>Controle (indicou)</th>
                  </tr>
                </thead>
                <tbody>
                  {semanas.map((s) => (
                    <tr key={s.semana}>
                      <td>{data(s.semana)}</td>
                      <td>{s.visitas}</td>
                      <td>{s.indicou == null ? "—" : `${s.indicou}%`}</td>
                      <td>{s.percentual == null ? "—" : `${s.percentual}%`}</td>
                      <td>{s.indicouControle == null ? "—" : `${s.indicouControle}%`}</td>
                    </tr>
                  ))}
                </tbody>
              </Tabela>
            </Bloco>
          )}

          {dados.ultimas.length > 0 && (
            <Bloco>
              <BlocoTitulo>Últimas respostas</BlocoTitulo>
              <Respostas>
                {dados.ultimas.slice(0, 5).map((r, i) => (
                  <li key={`${r.motor}-${i}`}>
                    <div>
                      <Etiqueta $neutra={!r.recomendou}>
                        {r.recomendou ? "indicou" : r.citou ? "só citou" : "não citou"}
                      </Etiqueta>
                      <Cinza> {nomeDoMotor(r.motor)}{r.marca ? " · pergunta pelo nome" : ""}</Cinza>
                    </div>
                    <Pergunta>{r.pergunta}</Pergunta>
                    {r.trecho && <Trecho>{r.trecho.length > 240 ? `${r.trecho.slice(0, 240)}…` : r.trecho}</Trecho>}
                    {r.fontes.length > 0 && (
                      <Fontes>
                        {r.fontes.slice(0, 3).map((f) => (
                          <a key={f.url} href={f.url} target="_blank" rel="noopener noreferrer">
                            {f.titulo || f.url}
                          </a>
                        ))}
                      </Fontes>
                    )}
                  </li>
                ))}
              </Respostas>
            </Bloco>
          )}
        </>
      )}
    </Cartao>
  );
}

export default PresencaEmIa;

const Cartao = styled.section`
  background: #fff; border: 1px solid var(--line); border-radius: 16px;
  padding: 22px; display: flex; flex-direction: column; gap: 16px; min-width: 0;
  @media (max-width: 600px) { padding: 16px; }
`;
const Topo = styled.div`display: flex; gap: 13px; align-items: flex-start; flex-wrap: wrap;`;
const Icone = styled.div`
  width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0;
  background: #F3EEFD; color: #5B2DB0;
  display: flex; align-items: center; justify-content: center;
  .material-icons { font-size: 23px; }
`;
const Nome = styled.h3`margin: 0; font-family: var(--font-sans); font-size: 16px; font-weight: 600; color: #1F1F1F;`;
const Desc = styled.p`margin: 3px 0 0; font-size: 13px; color: #5f6368; line-height: 19px;`;
const Selo = styled.span<{ $ligado: boolean }>`
  display: inline-flex; align-items: center; height: 26px; padding: 0 11px; border-radius: 999px;
  font-size: 12px; font-weight: 500; white-space: nowrap;
  background: ${(p) => (p.$ligado ? "#E6F4EA" : "#F1F3F4")};
  color: ${(p) => (p.$ligado ? "#137333" : "#5F6368")};
`;
const Bloco = styled.div`display: flex; flex-direction: column; gap: 8px; min-width: 0;`;
const BlocoTitulo = styled.h4`margin: 0; font-size: 13px; font-weight: 600; color: #2B2D31;`;
const Destaque = styled.div`
  display: flex; gap: 8px; align-items: flex-start;
  background: #F3EEFD; color: #3B1A80; border-radius: 10px; padding: 10px 12px; font-size: 13px;
`;
const Vazio = styled.p`
  margin: 0; font-size: 13px; color: #5f6368; background: #F7F9FC;
  border-radius: 10px; padding: 10px 12px;
`;
const Lista = styled.ul`
  list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column;
  li {
    display: flex; flex-wrap: wrap; gap: 4px 14px; align-items: center;
    padding: 8px 0; border-bottom: 1px solid #F1F3F4; font-size: 13px; color: #1F1F1F;
  }
  li:last-child { border-bottom: 0; }
`;
const Cinza = styled.span`color: #5f6368;`;
const Etiqueta = styled.span<{ $neutra?: boolean }>`
  display: inline-flex; align-items: center; height: 20px; padding: 0 8px; border-radius: 999px;
  font-size: 11px; font-weight: 600;
  background: ${(p) => (p.$neutra ? "#F1F3F4" : "#E6F4EA")};
  color: ${(p) => (p.$neutra ? "#5F6368" : "#137333")};
`;
const Tabela = styled.table`
  width: 100%; border-collapse: collapse; font-size: 13px; display: block; overflow-x: auto;
  th, td { text-align: left; padding: 7px 10px 7px 0; border-bottom: 1px solid #F1F3F4; white-space: nowrap; }
  th { font-size: 12px; font-weight: 600; color: #5f6368; }
`;
const Nota = styled.p`margin: 0; font-size: 12px; color: #5f6368;`;
const Respostas = styled.ul`
  list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px;
  li { border: 1px solid #F1F3F4; border-radius: 10px; padding: 10px 12px; font-size: 13px; min-width: 0; }
`;
const Pergunta = styled.div`margin-top: 4px; font-weight: 500; color: #1F1F1F;`;
const Trecho = styled.div`margin-top: 4px; color: #3c4043; overflow-wrap: anywhere;`;
const Fontes = styled.div`
  margin-top: 6px; display: flex; flex-wrap: wrap; gap: 4px 12px;
  a { font-size: 12px; color: #0B4FA8; overflow-wrap: anywhere; }
`;
